import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Mail, Send, Loader2, Info, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactUsersModal = ({ isOpen, onClose, selectedUsers, onSuccess }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [sending, setSending] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const token = localStorage.getItem('token');
    setSending(true);

    try {
      const response = await axios.post(
        `${API}/admin/contact-users`,
        {
          user_ids: selectedUsers.map(u => u.id),
          subject: subject.trim(),
          body: body.trim(),
          cta_url: ctaUrl.trim() || null,
          cta_text: ctaText.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onSuccess?.();
        handleClose();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send emails';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setBody('');
    setCtaUrl('');
    setCtaText('');
    setShowAdvanced(false);
    onClose();
  };

  const insertVariable = (variable) => {
    // Insert at cursor position or append
    const textarea = document.getElementById('contact-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = body.substring(0, start) + variable + body.substring(end);
      setBody(newBody);
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setBody(body + variable);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Contact Users
          </DialogTitle>
          <DialogDescription>
            Send a personalized email to {selectedUsers.length} selected user{selectedUsers.length > 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selected Users Preview */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Recipients ({selectedUsers.length}):</p>
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              {selectedUsers.slice(0, 10).map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-white border text-gray-700"
                >
                  {user.email}
                </span>
              ))}
              {selectedUsers.length > 10 && (
                <span className="text-xs text-gray-500">
                  +{selectedUsers.length - 10} more
                </span>
              )}
            </div>
          </div>

          {/* Variables Helper */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800 mb-1">Available Variables</p>
                <p className="text-xs text-blue-700 mb-2">
                  Click to insert or type directly. Variables will be replaced with user data.
                </p>
                <div className="flex flex-wrap gap-1">
                  {['{first_name}', '{last_name}', '{company_name}', '{email}'].map((variable) => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() => insertVariable(variable)}
                      className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md font-mono transition-colors"
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="contact-subject">Subject *</Label>
            <Input
              id="contact-subject"
              placeholder="e.g., Important Update from Snow Exchange"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="contact-subject-input"
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label htmlFor="contact-body">Message *</Label>
            <Textarea
              id="contact-body"
              placeholder="Hi {first_name},

We wanted to reach out regarding..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[200px] font-sans"
              data-testid="contact-body-textarea"
            />
            <p className="text-xs text-gray-500">
              Use blank lines to separate paragraphs. Single line breaks will be preserved.
            </p>
          </div>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            {showAdvanced ? '▼' : '▶'} Advanced Options (Call-to-Action Button)
          </button>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-3 pl-4 border-l-2 border-gray-200">
              <div className="space-y-2">
                <Label htmlFor="contact-cta-url">Button URL (optional)</Label>
                <Input
                  id="contact-cta-url"
                  placeholder="https://snow-exchange.com/..."
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-cta-text">Button Text (optional)</Label>
                <Input
                  id="contact-cta-text"
                  placeholder="e.g., Learn More →"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !body.trim()}
            style={{ backgroundColor: '#62D84E' }}
            data-testid="send-contact-email-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send to {selectedUsers.length} User{selectedUsers.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactUsersModal;
